/**
 * WHO Child Growth Standards — percentile reference curves, 0–60 months.
 *
 * ⚠️  NO CLINICAL CONCLUSION SHOULD EVER BE READ OFF THIS CHART.
 *
 * Rows 0–24 months are close approximations of the published standards; rows
 * 27–60 months are the official WHO percentile tables (weight-for-age 0–5 y,
 * height-for-age 2–5 y, boys and girls) from:
 *
 * They are close to the published WHO standards and are correct enough to draw
 * a recognisable percentile band behind a child's own line — which is what the
 * design asks for. They are NOT a substitute for the official tables, and no
 * one should read a clinical conclusion off this chart. Replace them with the
 * official WHO expanded tables (weight-for-age and length/height-for-age,
 * boys and girls, "Percentiles" .txt/.xlsx downloads) from:
 *
 *   https://www.who.int/tools/child-growth-standards/standards
 *
 * The official files give L, M and S values per day/week/month; deriving any
 * percentile from those (rather than storing three fixed curves) would also
 * let the chart draw P15/P85 and compute the child's exact percentile instead
 * of the rounded label shown today.
 *
 * One known seam: the 0–24 rows follow the WHO *length* (measured lying down)
 * values, the 27+ rows the *height* (standing) tables — the official standards
 * switch method at 2 years and standing measures ~0.7 cm shorter, so the
 * height band compresses slightly across the 24-month mark.
 *
 * Curves are sex-specific because the WHO standards are published separately
 * for boys and girls; using the wrong set shifts the band noticeably. Children
 * whose sex is recorded as 'unspecified' fall back to the girls' curves, which
 * sit slightly lower — chosen so the band is not flattering by accident.
 */

import type { ChildSex, GrowthKind } from '../lib/types';

/** Ages (in months) each row below corresponds to. */
const AGES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15, 18, 21, 24,
  27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60,
] as const;

/** [P3, P50, P97] per age in AGES. Weight in kg, length in cm. */
type Row = readonly [p3: number, p50: number, p97: number];

const WEIGHT_BOYS: readonly Row[] = [
  [2.5, 3.3, 4.4],
  [3.4, 4.5, 5.8],
  [4.3, 5.6, 7.1],
  [5.0, 6.4, 8.0],
  [5.6, 7.0, 8.7],
  [6.0, 7.5, 9.3],
  [6.4, 7.9, 9.8],
  [6.7, 8.3, 10.3],
  [6.9, 8.6, 10.7],
  [7.1, 8.9, 11.0],
  [7.4, 9.2, 11.4],
  [7.6, 9.4, 11.7],
  [7.7, 9.6, 12.0],
  [8.3, 10.3, 12.8],
  [8.8, 10.9, 13.7],
  [9.2, 11.5, 14.5],
  [9.7, 12.2, 15.3],
  // 27mo–60mo: official WHO weight-for-age 2–5y percentiles
  [10.2, 12.7, 15.9],
  [10.7, 13.3, 16.6],
  [11.1, 13.8, 17.3],
  [11.4, 14.3, 18.0],
  [11.8, 14.8, 18.7],
  [12.2, 15.3, 19.4],
  [12.5, 15.8, 20.1],
  [12.9, 16.3, 20.9],
  [13.3, 16.8, 21.6],
  [13.6, 17.3, 22.3],
  [13.9, 17.8, 23.0],
  [14.3, 18.3, 23.8],
];

const WEIGHT_GIRLS: readonly Row[] = [
  [2.4, 3.2, 4.2],
  [3.2, 4.2, 5.5],
  [3.9, 5.1, 6.6],
  [4.5, 5.8, 7.5],
  [5.0, 6.4, 8.2],
  [5.4, 6.9, 8.8],
  [5.7, 7.3, 9.3],
  [6.0, 7.6, 9.8],
  [6.3, 7.9, 10.2],
  [6.5, 8.2, 10.5],
  [6.7, 8.5, 10.9],
  [6.9, 8.7, 11.2],
  [7.0, 8.9, 11.5],
  [7.6, 9.6, 12.4],
  [8.1, 10.2, 13.2],
  [8.6, 10.9, 14.0],
  [9.0, 11.5, 14.8],
  // 27mo–60mo: official WHO weight-for-age 2–5y percentiles
  [9.6, 12.1, 15.4],
  [10.1, 12.7, 16.2],
  [10.5, 13.3, 17.0],
  [11.0, 13.9, 17.8],
  [11.4, 14.4, 18.6],
  [11.8, 15.0, 19.5],
  [12.1, 15.5, 20.3],
  [12.5, 16.1, 21.1],
  [12.9, 16.6, 22.0],
  [13.2, 17.2, 22.8],
  [13.6, 17.7, 23.6],
  [14.0, 18.2, 24.4],
];

const HEIGHT_BOYS: readonly Row[] = [
  [46.3, 49.9, 53.4],
  [51.1, 54.7, 58.4],
  [54.7, 58.4, 62.2],
  [57.6, 61.4, 65.3],
  [60.0, 63.9, 67.8],
  [61.9, 65.9, 69.9],
  [63.6, 67.6, 71.6],
  [65.1, 69.2, 73.2],
  [66.5, 70.6, 74.7],
  [67.7, 72.0, 76.2],
  [69.0, 73.3, 77.6],
  [70.2, 74.5, 78.9],
  [71.3, 75.7, 80.2],
  [74.4, 79.1, 83.9],
  [77.2, 82.3, 87.3],
  [79.7, 85.1, 90.5],
  [82.1, 87.8, 93.6],
  // 27mo–60mo: official WHO height-for-age 2–5y percentiles (standing height —
  // see the length/height seam note at the top of this file)
  [83.5, 89.6, 95.7],
  [85.5, 91.9, 98.3],
  [87.4, 94.1, 100.8],
  [89.1, 96.1, 103.1],
  [90.8, 98.0, 105.2],
  [92.4, 99.9, 107.3],
  [93.9, 101.6, 109.3],
  [95.4, 103.3, 111.2],
  [96.9, 105.0, 113.1],
  [98.4, 106.7, 115.0],
  [99.8, 108.3, 116.8],
  [101.2, 110.0, 118.7],
];

const HEIGHT_GIRLS: readonly Row[] = [
  [45.6, 49.1, 52.7],
  [50.0, 53.7, 57.4],
  [53.2, 57.1, 60.9],
  [55.8, 59.8, 63.8],
  [58.0, 62.1, 66.2],
  [59.9, 64.0, 68.2],
  [61.5, 65.7, 70.0],
  [62.9, 67.3, 71.6],
  [64.3, 68.7, 73.2],
  [65.6, 70.1, 74.7],
  [66.8, 71.5, 76.1],
  [68.0, 72.8, 77.5],
  [69.2, 74.0, 78.9],
  [72.4, 77.5, 82.7],
  [75.2, 80.7, 86.2],
  [77.7, 83.7, 89.6],
  [80.0, 86.4, 92.9],
  // 27mo–60mo: official WHO height-for-age 2–5y percentiles (standing height —
  // see the length/height seam note at the top of this file)
  [81.9, 88.3, 94.6],
  [84.0, 90.7, 97.3],
  [86.0, 92.9, 99.8],
  [87.9, 95.1, 102.2],
  [89.7, 97.1, 104.5],
  [91.4, 99.0, 106.7],
  [93.0, 100.9, 108.8],
  [94.6, 102.7, 110.8],
  [96.2, 104.5, 112.8],
  [97.6, 106.2, 114.7],
  [99.1, 107.8, 116.6],
  [100.5, 109.4, 118.4],
];

function tableFor(kind: GrowthKind, sex: ChildSex): readonly Row[] {
  const boys = sex === 'male';
  if (kind === 'weight') return boys ? WEIGHT_BOYS : WEIGHT_GIRLS;
  return boys ? HEIGHT_BOYS : HEIGHT_GIRLS;
}

export const WHO_MAX_MONTHS = AGES[AGES.length - 1];

export type Percentile = 'p3' | 'p50' | 'p97';

const INDEX: Record<Percentile, 0 | 1 | 2> = { p3: 0, p50: 1, p97: 2 };

/**
 * Reference value at a fractional age, linearly interpolated between table
 * rows. Ages outside 0–60 months clamp to the nearest end.
 */
export function whoValueAt(
  kind: GrowthKind,
  sex: ChildSex,
  percentile: Percentile,
  months: number,
): number {
  const table = tableFor(kind, sex);
  const col = INDEX[percentile];
  const m = Math.max(AGES[0], Math.min(WHO_MAX_MONTHS, months));

  for (let i = 0; i < AGES.length - 1; i += 1) {
    const a0 = AGES[i];
    const a1 = AGES[i + 1];
    if (m >= a0 && m <= a1) {
      const t = a1 === a0 ? 0 : (m - a0) / (a1 - a0);
      return table[i][col] + t * (table[i + 1][col] - table[i][col]);
    }
  }
  return table[table.length - 1][col];
}

/** Sample a percentile curve across a month window, for drawing an SVG path. */
export function whoSeries(
  kind: GrowthKind,
  sex: ChildSex,
  percentile: Percentile,
  fromMonths: number,
  toMonths: number,
  steps = 24,
): { months: number; value: number }[] {
  const out: { months: number; value: number }[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const months = fromMonths + ((toMonths - fromMonths) * i) / steps;
    out.push({ months, value: whoValueAt(kind, sex, percentile, months) });
  }
  return out;
}

/**
 * Rough percentile label for a measurement, e.g. "50パーセンタイル".
 *
 * Interpolates between the three curves we have, so it is a coarse estimate —
 * genuinely computing a percentile needs the WHO LMS values (see the note at
 * the top of this file). Deliberately reports bands rather than false
 * precision: "3パーセンタイル未満" / "97パーセンタイル以上" at the extremes.
 */
export function estimatePercentileLabel(
  kind: GrowthKind,
  sex: ChildSex,
  months: number,
  value: number,
): string {
  const p3 = whoValueAt(kind, sex, 'p3', months);
  const p50 = whoValueAt(kind, sex, 'p50', months);
  const p97 = whoValueAt(kind, sex, 'p97', months);

  if (value <= p3) return '3パーセンタイル未満';
  if (value >= p97) return '97パーセンタイル以上';

  const pct =
    value < p50 ? 3 + ((value - p3) / (p50 - p3)) * 47 : 50 + ((value - p50) / (p97 - p50)) * 47;

  return `${Math.round(pct)}パーセンタイル`;
}
